import {
  CircularProgress,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import unboundActions from "domain/core/actions";
import {
  getBillEstimate,
  getBillEstimateFetchStatus,
  getMonthlyElectricityUsage,
  getSelectedProduct,
  getSignupLitres,
  getUsageType,
} from "domain/core/selectors";
import { UsageType } from "domain/core/types";
import { humanReadableUsagePeriod } from "lib/util";
import { useActions, useReduxState } from "re-reduced";
import React, { ChangeEvent, useCallback, useState } from "react";
import { styled } from "styled-components";
import {
  ControlledCard,
  HeroTypography,
  RowLayoutLeft,
} from "ui/components/StyledComponents";
import { ExtraPlanPageText } from "ui/compounds/planDetails/ExtraPlanPageText";
import { PADDING } from "ui/theme";

const SCircularProgress = styled(CircularProgress)`
  margin: 1rem;
`;

const CardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SRowLayoutLeft = styled(RowLayoutLeft)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${PADDING.medium};

  > * {
    margin-right: ${PADDING.medium};
  }
`;

const SSelect = styled(Select)`
  font-weight: 600;
`;

interface FuelRewardsPlanProps {
  onPlanDetailsDialogOpen: () => void;
  usagePeriod: number;
  setUsagePeriod: (usagePeriod: number) => void;
  GSTInclusive: boolean;
  setGSTInclusive: (GSTInclusive: boolean) => void;
}

const stateSelectorMap = {
  billEstimate: getBillEstimate,
  billEstimateFetchStatus: getBillEstimateFetchStatus,
  campaignLitres: getSignupLitres,
  monthlyUsage: getMonthlyElectricityUsage,
  selectedProduct: getSelectedProduct,
  usageType: getUsageType,
};

export function FuelRewardsPlan(props: FuelRewardsPlanProps) {
  const reduxState = useReduxState(stateSelectorMap);
  const [electricityUsage, setElectricityUsage] = useState<string>(
    reduxState.monthlyUsage.toFixed(2),
  );
  const [electricityUsageError, setElectricityUsageError] =
    useState<string>(null);
  const usagePeriodName = humanReadableUsagePeriod(Number(props.usagePeriod));

  const actions = useActions(unboundActions);

  const handleGSTChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // The input linked to this is a select which doesn't
      // accept booleans so we need to coerce a different type instead.
      const checked = Boolean(event.target.value);
      props.setGSTInclusive(checked);
    },
    [],
  );

  const handleUsageTypeChange = useCallback(
    (event: SelectChangeEvent<UsageType>) => {
      const usageType = event.target.value as UsageType;
      actions.setUsageType(usageType);
      Number(electricityUsage) &&
        actions.fetchBillingEstimate({
          usageType: usageType,
          locationClassification:
            reduxState.billEstimate?.location_classification,
          consumption: electricityUsage,
          duration: props.usagePeriod,
        });
    },
    [electricityUsage, props.usagePeriod],
  );

  const handleUsagePeriodChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const period = event.target.value as number;
      props.setUsagePeriod(period);
      Number(electricityUsage) &&
        actions.fetchBillingEstimate({
          usageType: reduxState.usageType,
          locationClassification:
            reduxState.billEstimate?.location_classification,
          consumption: electricityUsage,
          duration: period,
        });
    },
    [electricityUsage, reduxState.usageType],
  );

  const handleUsageValueBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      event?.preventDefault();
      const usageValue = event.target.value;
      setElectricityUsage(usageValue);
      if (Number(usageValue)) {
        setElectricityUsageError(null);
        actions.fetchBillingEstimate({
          usageType: reduxState.usageType,
          locationClassification:
            reduxState.billEstimate?.location_classification,
          consumption: usageValue,
          duration: props.usagePeriod,
        });
      } else {
        setElectricityUsageError("Please enter a numerical usage value");
      }
    },
    [props.usagePeriod, reduxState.usageType],
  );

  const renderResult = useCallback(() => {
    const estimatedBill = reduxState.billEstimate?.total_estimate ?? "0";

    const billEstimateValue = () => {
      if (props.GSTInclusive) {
        return (parseFloat(estimatedBill) * 1.15).toFixed(2);
      }
      return parseFloat(estimatedBill).toFixed(2);
    };

    const getOngoingFuelReward = () => {
      const est = parseFloat(estimatedBill) * 1.15;
      return (est / 20).toFixed(2);
    };
    if (reduxState.billEstimateFetchStatus === "Pending") {
      return <SCircularProgress />;
    }

    // Successfully found address and bill estimate(s) (can be 1 or more)
    if (reduxState.billEstimateFetchStatus === "Fulfilled") {
      return (
        <div>
          <CardContainer>
            <ControlledCard>
              <Typography variant="h4">Estimated electricity bill</Typography>
              <HeroTypography>${billEstimateValue()}</HeroTypography>
              <Typography variant="caption" component="p">
                A typical {humanReadableUsagePeriod(props.usagePeriod)} bill in
                your area.* GST {props.GSTInclusive ? "inclusive" : "exclusive"}
                .
              </Typography>
            </ControlledCard>
            <ControlledCard>
              <Typography variant="h4">Fuel for joining</Typography>
              <HeroTypography>{reduxState.campaignLitres}L</HeroTypography>
              <Typography variant="caption" component="p">
                Choose between 91 Unleaded, 95 Premium or Diesel to be added
                into your Sharetank after you join Z at home - tanks heaps!
              </Typography>
            </ControlledCard>
            <ControlledCard>
              <Typography variant="h4">Estimated fuel back</Typography>
              <HeroTypography>{getOngoingFuelReward()}L</HeroTypography>
              <Typography variant="caption" component="p">
                Download the Z App to access your fuel using Sharetank. Each
                bill you’ll receive 5L for every $100 spent on power.
              </Typography>
            </ControlledCard>
          </CardContainer>
        </div>
      );
    }
  }, [
    props.GSTInclusive,
    handleGSTChange,
    props.usagePeriod,
    reduxState.billEstimate,
  ]);

  return (
    <>
      <ExtraPlanPageText />
      <div>
        <SRowLayoutLeft>
          <Typography variant="subtitle1">Estimate based on:</Typography>
          <SSelect
            autoWidth
            defaultValue={reduxState.usageType}
            onChange={handleUsageTypeChange}
          >
            <MenuItem value={UsageType.STANDARD}>
              {UsageType.STANDARD} power user
            </MenuItem>
            <MenuItem value={UsageType.LOW}>
              {UsageType.LOW} power user
            </MenuItem>
          </SSelect>
          <TextField
            sx={{ minWidth: "19rem" }}
            variant="outlined"
            error={!!electricityUsageError}
            label={
              !electricityUsageError
                ? `Enter your ${usagePeriodName} usage`
                : electricityUsageError
            }
            defaultValue={electricityUsage}
            inputProps={{ inputMode: "numeric", pattern: /^\d*\.?\d*$/ }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">kWh</InputAdornment>
              ),
            }}
            onBlur={handleUsageValueBlur}
          />
          <SSelect
            autoWidth
            onChange={handleUsagePeriodChange}
            value={props.usagePeriod}
          >
            <MenuItem value={30}>per month</MenuItem>
            <MenuItem value={14}>per fortnight</MenuItem>
            <MenuItem value={7}>per week</MenuItem>
          </SSelect>
          <SSelect
            autoWidth
            defaultValue={Number(props.GSTInclusive)}
            onChange={handleGSTChange}
          >
            <MenuItem value={1}>incl. gst</MenuItem>
            <MenuItem value={0}>excl. gst</MenuItem>
          </SSelect>
          <Link onClick={props.onPlanDetailsDialogOpen}>
            *Pricing and plan details
          </Link>
        </SRowLayoutLeft>
      </div>
      {renderResult()}
    </>
  );
}
